import { useEffect } from 'react'

const SCROLL_DELTA = 8
const TOP_REVEAL_OFFSET = 12

/**
 * Hide project chrome (header + tabs) when scrolling down the board body,
 * and smoothly reveal it again when scrolling up.
 */
const useHideChromeOnScroll = (scrollElRef) => {
    useEffect(() => {
        let cancelled = false
        let frame = 0
        let lastScrollTop = 0
        let scrollEl = null
        let retryTimer = null

        const getChrome = () =>
            document.querySelector('[data-project-chrome="true"]') ||
            document.getElementById('project-chrome')

        const setHidden = (hidden) => {
            const chrome = getChrome()
            if (!chrome) return
            if (hidden) {
                chrome.setAttribute('data-chrome-hidden', 'true')
            } else {
                chrome.removeAttribute('data-chrome-hidden')
            }
        }

        const onScroll = () => {
            if (!scrollEl || frame) return

            frame = window.requestAnimationFrame(() => {
                frame = 0
                const scrollTop = scrollEl.scrollTop
                const delta = scrollTop - lastScrollTop

                if (scrollTop <= TOP_REVEAL_OFFSET) {
                    setHidden(false)
                } else if (delta > SCROLL_DELTA) {
                    setHidden(true)
                } else if (delta < -SCROLL_DELTA) {
                    setHidden(false)
                }

                lastScrollTop = scrollTop
            })
        }

        const attach = () => {
            if (cancelled) return false
            scrollEl = scrollElRef?.current
            if (!scrollEl) return false

            lastScrollTop = scrollEl.scrollTop
            scrollEl.addEventListener('scroll', onScroll, { passive: true })
            return true
        }

        if (!attach()) {
            retryTimer = window.setTimeout(() => {
                if (!attach()) {
                    retryTimer = window.setTimeout(attach, 120)
                }
            }, 0)
        }

        return () => {
            cancelled = true
            if (retryTimer) window.clearTimeout(retryTimer)
            if (frame) window.cancelAnimationFrame(frame)
            if (scrollEl) {
                scrollEl.removeEventListener('scroll', onScroll)
            }
            setHidden(false)
        }
    }, [scrollElRef])
}

export default useHideChromeOnScroll
